import { Card, Skeleton } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useEffect, useRef } from "react";
import { useRouteLoaderData } from "react-router";

import { useClientContext } from "~/contexts/client-context";
import { usePluginContext } from "~/contexts/plugin-context";
import type { loader } from "~/root";
import { useGtmContext } from "~/shared/utils/gtm-sdk/provider";
import { useAuthModals } from "~/widgets/auth-modals";
import type {
  GenerateNicknameRegistrationPluginProps,
  PluginInstance,
} from "~/widgets/generate-nickname-plugin/types";

export const GenerateNicknamePlugin: FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { affiliate, language, authFeatures, siteSdk, featureFlags } =
    useClientContext();
  const { gtm } = useGtmContext();
  const data = useRouteLoaderData<typeof loader>("root");
  const { open } = useAuthModals();
  const registrationData = authFeatures?.registration?.[0];
  const pluginSettings = data?.pluginIntegrations?.casinoFastRegistration;
  const isFastRegistrationFeatureEnabled = pluginSettings?.ftd_enabled ?? false;
  const standaloneUrl = pluginSettings?.standalone_url ?? "";
  const isSkipSuccessRegistrationScreen =
    pluginSettings?.skip_success_registration_screen ?? false;
  const isPreDepBonusEnabled = featureFlags.getItem(
    "bonusBeforeFirstDep",
  ).enabled;
  const initialParams: GenerateNicknameRegistrationPluginProps = {
    apiUrl:
      pluginSettings?.api_url ?? "https://casino-registration.jack-casino.com",

    startScreen: "registration",
    referralCode: affiliate,
    onRegister: async (params) => {
      const { playerID } = params;
      gtm.signUp({ eventTime: new Date(), playerID });
      await siteSdk.setAuthCookie(params);
      if (isFastRegistrationFeatureEnabled) {
        if (isPreDepBonusEnabled) {
          open("preDepBonus");
        } else {
          window.location.href = `/${language}/ftd`;
        }
      } else {
        const { redirectUrl } = await siteSdk.getRoomUrl(
          language,
          "casino/games",
        );

        window.location.href = redirectUrl;
      }
    },
    onGoToLogin: () => {
      console.info("go to login");
    },

    termsUrl: pluginSettings?.terms_url ?? "",
    redirectTimeout:
      isPreDepBonusEnabled && isFastRegistrationFeatureEnabled ? 0 : undefined,
    locale: language,
    textContent: {
      registration: {
        form_title:
          registrationData?.form_title ??
          "Register now and get all your bonuses!",
        form_password_input_label:
          registrationData?.form_password_input_label ?? "Password",
        form_promo_code_button_text:
          registrationData?.form_promo_code_button_text ??
          "I have a promo code",
        form_promo_code_input_label:
          registrationData?.form_promo_code_input_label ?? "Promo code",
        validation_invalid_email:
          registrationData?.validation_invalid_email ?? "Invalid email address",
        validation_required:
          registrationData?.validation_required ?? "Required field",
        validation_password_max_width:
          registrationData?.validation_password_max_width ??
          "Must be at most 20 characters",
        validation_password_min_width:
          registrationData?.validation_password_min_width ??
          "Must be at least 5 characters",
        form_submit_button_text:
          registrationData?.form_submit_button_text ?? "Create Account",
        form_email_input_label:
          registrationData?.form_email_input_label ?? "Email",

        validation_email_max_length:
          registrationData?.validation_email_max_length ??
          "Must be at most 30 characters",
        validation_email_min_length:
          registrationData?.validation_email_min_length ??
          "Must be at least 6 characters",
        validation_password_contain_spaces:
          registrationData?.validation_password_contain_spaces ??
          "Password must not contain spaces",

        // специальные поля
        form_terms_description_label:
          pluginSettings?.translations?.[0]?.form_terms_description_label ??
          "Terms and conditions",

        form_terms_description_text:
          pluginSettings?.translations?.[0]?.form_terms_description_text ??
          "By continuing, you certify that you are the minimum legal age for gambling in my jurisdiction, and that you have read and agreed to the",
      },
      registrationCompleted: {
        open_app_button: registrationData?.open_app_button ?? "Open App",
        redirect_hint:
          registrationData?.redirect_hint ??
          "If you are not redirected, please click this button",
        redirect_message:
          registrationData?.redirect_message ??
          "Please, wait for the redirect to the room.",
        success_message:
          registrationData?.success_message ??
          "You are successfully registered.",
        success_title: registrationData?.success_title ?? "Congratulations!",
      },
    },
    isSkipSuccessRegistrationScreen,
  };

  const { loadScript, isLoading } = usePluginContext();

  useEffect(() => {
    if (!standaloneUrl) {
      return;
    }

    let instance:
      | undefined
      | PluginInstance<GenerateNicknameRegistrationPluginProps>;

    const init = async (): Promise<void> => {
      await loadScript(standaloneUrl);

      if (!ref.current) return;

      instance = window?.QWSCasinoRegistration?.mount(
        ref.current,
        initialParams,
      );
    };

    void init();

    return () => {
      instance?.unmount?.();
    };
  }, [ref, initialParams]);

  return isLoading ? (
    <Card
      border={{ radius: "card" }}
      variant="secondary"
      bg="background-tetriary"
      md={{ maxWidth: 525 }}
      minWidth={312}
      maxWidth={440}
      d={{ maxWidth: 525, minWidth: 500 }}
    >
      <Skeleton radius={8} width={"100%"} height={580} />
    </Card>
  ) : (
    <div ref={ref} id="generate-nickname-registration" />
  );
};
